import { orderAmountFormatter } from './deal-series.util';

export interface SinkingFundScheduleEntry {
  date: string;
  amount: number;
}

export const TERM_BOND_PAR_AMOUNT_COLOR = '#4da3ff';

export function parseSinkingFundSchedule(
  scheduleJson: string | null | undefined,
): SinkingFundScheduleEntry[] {
  if (!scheduleJson?.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(scheduleJson) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const record = item as Record<string, unknown>;
        const date = typeof record.date === 'string' ? record.date.trim() : '';
        const amount = record.amount;
        if (!date || amount === null || amount === undefined) {
          return null;
        }
        const numericAmount =
          typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ''));
        if (Number.isNaN(numericAmount) || numericAmount <= 0) {
          return null;
        }
        return { date, amount: numericAmount };
      })
      .filter((entry): entry is SinkingFundScheduleEntry => entry !== null);
  } catch {
    return [];
  }
}

export function isTermBondMaturity(scheduleJson: string | null | undefined): boolean {
  return parseSinkingFundSchedule(scheduleJson).length > 0;
}

/** API / stored dates are ISO-like (yyyy-MM-dd); display as MM/YYYY per maturity grid. */
export function formatSinkingFundMaturityDate(dateStr: string): string {
  const trimmed = dateStr.trim();
  const isoMatch = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[1]}`;
  }
  const slashMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    return `${slashMatch[1]}/${slashMatch[3]}`;
  }
  return trimmed;
}

export function formatSinkingFundAmount(amount: number | string): string {
  return orderAmountFormatter(amount);
}

export function computeSinkingFundPanelLayout(
  mouseEvent: MouseEvent,
  viewportHeight: number = window.innerHeight,
  viewportWidth: number = window.innerWidth,
): string {
  const maxPanelHeight = 400;
  const padding = 12;
  const minPanelWidth = 220;
  const anchorX = mouseEvent.clientX;
  const anchorY = mouseEvent.clientY;
  const spaceAbove = anchorY - padding;
  const spaceBelow = viewportHeight - anchorY - padding;
  const growUpward = spaceAbove >= spaceBelow;
  const panelMaxHeight = Math.min(
    maxPanelHeight,
    Math.max(0, growUpward ? spaceAbove : spaceBelow),
  );
  const left = Math.max(padding, Math.min(anchorX, viewportWidth - minPanelWidth - padding));

  if (growUpward) {
    return `left:${left}px;bottom:${viewportHeight - anchorY}px;max-height:${panelMaxHeight}px;`;
  }

  return `left:${left}px;top:${anchorY}px;max-height:${panelMaxHeight}px;`;
}
